import { orgAPI } from "@/api/api";
import { usePatchFileRecordRegion } from "@/api/fileRecords";
import Sentry from "@/integrations/Sentry";
import { useFileRecordExport } from "@/panes/file-record-detail/FileRecordExportContext";
import { FileRecordSelectionActionOverlay } from "@/panes/file-record-detail/FileRecordSelectionActionOverlay";
import {
  attachIframeTextSelectionListeners,
  type IframeTextSelectionState,
} from "@/panes/file-record-detail/useDomTextSelectionAction";
import { usePushFileRecordTextSelectionToChat } from "@/panes/file-record-detail/usePushFileRecordTextSelectionToChat";
import { cn } from "@/utils";
import type { FileRecord } from "@cohesive/api-client";
import type { SelectionAnchorRect } from "@cohesive/ui-components";
import {
  computeHtmlSrcDocFingerprint,
  morphPreparedHtmlIntoDocument,
  prepareSrcDocHtml,
} from "@cohesive/ui-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { getFileRecordName } from "../contentType";
import { LoadingOverlay } from "../LoadingOverlay";
import { useFileRecordVersionPreview } from "../versioning/FileRecordVersioningContext";
import { PUBLIC_URL_REGEX, useHtmlReferencedIds } from "./htmlReferencedIds";
import { useHtmlIslandAutosave } from "./useHtmlIslandAutosave";

/**
 * After a local save, suppress SSE-triggered re-fetches for this long.
 * The DOM already shows the correct content; re-fetching would cause a
 * needless iframe reload with visually identical content.
 */
const IGNORE_SSE_AFTER_SAVE_MS = 3_000;

// Non-typed keys presentation frameworks (reveal.js, impress.js, …) bind for
// slide nav. Only these are suppressed while editing — never letters like N/P/F
// that frameworks also bind but the user needs to type.
const PRESENTATION_NAV_KEYS = new Set([
  " ",
  "Spacebar",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
]);

/**
 * HTML file record viewer with island-based inline editing.
 *
 * Islands are `[data-edit-id]` elements annotated by the agent. The prepared
 * `srcDoc` injects `contenteditable="true"` on those elements so the user
 * edits text in-place. Changes autosave after 5s of inactivity; pending
 * count and save status appear in the pane header beside Publish.
 *
 * SSE-triggered re-fetches are suppressed briefly after a local save to avoid
 * an unnecessary reload.
 *
 * Content is delivered via iframe `srcDoc` on first load. Subsequent updates
 * morph the live document via Idiomorph when `computeHtmlSrcDocFingerprint`
 * changes (content or presign-fallback state), not when presigned URL tokens
 * rotate.
 *
 * Before setting `srcDoc`, `rewritePublicFileUrls` replaces any
 * `https://file.{env}.cohesive.ai/public/{id}` references with short-lived
 * presigned URLs so embedded assets resolve correctly. The raw body is also
 * forwarded to the export context via `setHtmlSource`.
 */

/**
 * Rewrite `https://file.{env}.cohesive.ai/public/{id}` references in `html`
 * to short-lived presigned CloudFront URLs. Uses the server's bulk-presign
 * endpoint so the rewrite is a single round trip regardless of how many
 * referenced files the document has. Per-id failures (record deleted, still
 * processing, permission denied, …) leave the original URL untouched so the
 * iframe can still render whatever public copy may exist.
 *
 * `knownMissingIds` accumulates ids that should no longer be presigned. An
 * id lands here two ways: (1) the bulk-presign endpoint reports a per-item
 * error for it, or (2) the iframe reports that its presigned asset URL 404'd.
 * The delivery effect re-runs on every SSE re-fetch, so without this an id
 * whose asset is gone would be re-presigned and re-requested on every content
 * update forever. Skipped ids keep their original embedded public URL —
 * stable and CloudFront-cacheable, unlike the rotating presigned URL.
 *
 * Returns the rewritten HTML plus a `presignedUrlToId` map so the caller can
 * trace an iframe asset 404 back to the file-record id that produced it.
 */
interface RewriteResult {
  html: string;
  /** Presigned URL → file-record id, for the rewrites applied this call. */
  presignedUrlToId: Map<string, string>;
}

async function rewritePublicFileUrls(
  html: string,
  ids: string[],
  fileRecordId: string,
  knownMissingIds: Set<string>,
): Promise<RewriteResult> {
  const empty = (): RewriteResult => ({ html, presignedUrlToId: new Map() });
  if (ids.length === 0) return empty();

  const idsToPresign = ids.filter((id) => !knownMissingIds.has(id));
  if (idsToPresign.length === 0) return empty();

  let replacements: Map<string, string>;
  try {
    const items = await orgAPI.fileRecords.bulkPresignURLs(idsToPresign);
    replacements = new Map();
    for (const item of items) {
      if (item.url) {
        replacements.set(item.file_record_id, item.url);
      } else {
        knownMissingIds.add(item.file_record_id);
      }
    }
  } catch (err) {
    Sentry.captureException(err, {
      tags: { feature: "html-publish-rewrite" },
      extra: {
        fileRecordId,
        referencedIdCount: ids.length,
        stage: "bulk-presign",
      },
    });
    return empty();
  }

  if (replacements.size === 0) return empty();

  const presignedUrlToId = new Map<string, string>();
  const rewritten = html.replace(PUBLIC_URL_REGEX, (original, id: string) => {
    const replacement = replacements.get(id);
    if (!replacement) return original;
    presignedUrlToId.set(replacement, id);
    return replacement;
  });
  return { html: rewritten, presignedUrlToId };
}

export function HtmlContent({ record }: { record: FileRecord }) {
  const { mutateAsync: patchRegions } = usePatchFileRecordRegion(record.id);
  const { setHtmlSource } = useFileRecordExport();
  const { previewMediaUrl, isPreviewing } = useFileRecordVersionPreview();
  const [previewLoaded, setPreviewLoaded] = useState(false);

  const { data: htmlMeta, isError: bodyFetchFailed } = useHtmlReferencedIds(
    record,
    { enabled: !isPreviewing },
  );

  const [isFirstLoading, setIsFirstLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [srcDoc, setSrcDoc] = useState<string | null>(null);
  const [activeIslandId, setActiveIslandId] = useState<string | null>(null);
  const [iframeSelectionText, setIframeSelectionText] = useState("");
  const [iframeSelectionAnchor, setIframeSelectionAnchor] =
    useState<SelectionAnchorRect | null>(null);

  const { enabled: textSelectionEnabled, pushSelection } =
    usePushFileRecordTextSelectionToChat({
      fileRecordId: record.id,
      fileRecordName: getFileRecordName(record),
    });

  const handleIframeSelectionChange = useCallback(
    ({ selectedText, anchorRect }: IframeTextSelectionState) => {
      setIframeSelectionText(selectedText);
      setIframeSelectionAnchor(anchorRect);
    },
    [],
  );

  const clearIframeSelection = useCallback(() => {
    setIframeSelectionText("");
    setIframeSelectionAnchor(null);
  }, []);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeCleanupRef = useRef<(() => void) | null>(null);
  const hasEverShownContentRef = useRef(false);
  const srcDocFingerprintRef = useRef<string | null>(null);
  const activeIslandIdRef = useRef<string | null>(null);
  const justSavedTimestampRef = useRef<number>(0);
  const knownMissingPresignIdsRef = useRef<Set<string>>(new Set());
  const presignedUrlToIdRef = useRef<Map<string, string>>(new Map());
  const [presignRetryNonce, setPresignRetryNonce] = useState(0);

  const handleAssetErrorRef = useRef<(url: string) => void>(() => {});
  const flushDirtyRegionsRef = useRef<() => void>(() => {});
  const autosaveHandlersRef = useRef({
    handleIslandFocus: (_editId: string, _innerHTML: string) => {},
    handleIslandInput: (_editId: string, _innerHTML: string) => {},
    handleIslandFocusOut: (_editId: string, _innerHTML: string) => {},
    getDirtyEditIds: (): ReadonlySet<string> => new Set<string>(),
    resetAutosave: (_options?: { flush?: boolean }) => {},
  });

  const getIframeDoc = useCallback(
    () =>
      iframeRef.current?.contentDocument ??
      iframeRef.current?.contentWindow?.document ??
      null,
    [],
  );

  const onSaveSuccess = useCallback(() => {
    justSavedTimestampRef.current = Date.now();
  }, []);

  const {
    getDirtyEditIds,
    handleIslandFocus,
    handleIslandInput,
    handleIslandFocusOut,
    flushDirtyRegions,
    resetAutosave,
  } = useHtmlIslandAutosave({
    getIframeDoc,
    patchRegions,
    onSaveSuccess,
  });

  useEffect(() => {
    flushDirtyRegionsRef.current = flushDirtyRegions;
    autosaveHandlersRef.current = {
      handleIslandFocus,
      handleIslandInput,
      handleIslandFocusOut,
      getDirtyEditIds,
      resetAutosave,
    };
  }, [
    flushDirtyRegions,
    handleIslandFocus,
    handleIslandInput,
    handleIslandFocusOut,
    getDirtyEditIds,
    resetAutosave,
  ]);

  useEffect(() => {
    knownMissingPresignIdsRef.current = new Set();
    presignedUrlToIdRef.current = new Map();
    srcDocFingerprintRef.current = null;
    hasEverShownContentRef.current = false;
    setSrcDoc(null);
    setIframeLoaded(false);
    clearIframeSelection();
    resetAutosave();

    // Flush pending edits for the outgoing record before `patchRegions` switches.
    return () => {
      try {
        iframeCleanupRef.current?.();
      } catch {}
      iframeCleanupRef.current = null;
      resetAutosave({ flush: true });
    };
  }, [record.id, resetAutosave, clearIframeSelection]);

  useEffect(() => {
    handleAssetErrorRef.current = (url: string) => {
      const id = presignedUrlToIdRef.current.get(url);
      if (!id || knownMissingPresignIdsRef.current.has(id)) return;
      knownMissingPresignIdsRef.current.add(id);
      setPresignRetryNonce((n) => n + 1);
    };
  }, []);

  useEffect(() => {
    if (isPreviewing) {
      setPreviewLoaded(false);
    }
  }, [isPreviewing, previewMediaUrl]);

  useEffect(() => {
    if (isPreviewing) {
      return;
    }
    srcDocFingerprintRef.current = null;
    hasEverShownContentRef.current = false;
    setSrcDoc(null);
    setIframeLoaded(false);
  }, [isPreviewing]);

  useEffect(() => {
    activeIslandIdRef.current = activeIslandId;
  }, [activeIslandId]);

  const getEditableIsland = useCallback(
    (target: EventTarget | null): HTMLElement | null => {
      // Duck-type across the iframe boundary: `instanceof Element` uses the
      // parent realm's constructor and is always false for srcDoc nodes.
      if (!target || !("nodeType" in target)) return null;
      const node = target as Node;
      const el =
        node.nodeType === 1
          ? (node as Element)
          : ((node as ChildNode).parentElement ?? null);
      if (!el || typeof el.closest !== "function") return null;
      return el.closest<HTMLElement>("[data-edit-id]:not([data-no-edit])");
    },
    [],
  );

  const setupIframeInteractions = useCallback(() => {
    const iframeEl = iframeRef.current;
    const doc = iframeEl?.contentDocument ?? iframeEl?.contentWindow?.document;
    if (!iframeEl || !doc?.body) return;

    try {
      iframeCleanupRef.current?.();
    } catch {}
    iframeCleanupRef.current = null;

    const onFocusIn = (e: FocusEvent) => {
      const el = getEditableIsland(e.target);
      if (!el) return;
      const editId = el.dataset.editId;
      if (!editId) return;
      autosaveHandlersRef.current.handleIslandFocus(editId, el.innerHTML);
      setActiveIslandId(editId);
    };

    const onFocusOut = (e: FocusEvent) => {
      const fromIsland = getEditableIsland(e.target);
      const toIsland = getEditableIsland(e.relatedTarget);
      if (fromIsland) {
        const editId = fromIsland.dataset.editId;
        if (editId) {
          autosaveHandlersRef.current.handleIslandFocusOut(
            editId,
            fromIsland.innerHTML,
          );
        }
      }
      if (fromIsland && toIsland) return;
      setActiveIslandId(null);
    };

    const onInput = (e: Event) => {
      const el = getEditableIsland(e.target);
      if (!el) return;
      const editId = el.dataset.editId;
      if (!editId) return;
      autosaveHandlersRef.current.handleIslandInput(editId, el.innerHTML);
    };

    const onKeyDown = (e: Event) => {
      const el = getEditableIsland(e.target);
      if (!el) return;
      e.stopPropagation();

      const ke = e as KeyboardEvent;
      if (ke.key === "Enter" && !ke.shiftKey) {
        e.preventDefault();
        doc.execCommand("insertLineBreak");
      }
      if (ke.key === "s" && (ke.metaKey || ke.ctrlKey)) {
        e.preventDefault();
        flushDirtyRegionsRef.current();
      }
    };

    const onPaste = (e: Event) => {
      const el = getEditableIsland(e.target);
      if (!el) return;
      e.preventDefault();
      const text =
        (e as ClipboardEvent).clipboardData?.getData("text/plain") ?? "";
      doc.execCommand("insertText", false, text);
    };

    // While editing an island, suppress the keys a presentation framework
    // (reveal.js, impress.js, …) grabs for slide nav so they drive the caret /
    // insert text instead. Intercept in the CAPTURE phase on the iframe window —
    // ahead of
    // the document/bubble listeners the presentation registers — so they never
    // reach its slide-nav handler. We only stop propagation (not the default),
    // so editing keeps normal caret/text behaviour. Gated on an island actually
    // being focused, so view mode keeps full slide navigation.
    const navKeyTarget: Window | Document = iframeEl.contentWindow ?? doc;
    const onNavKeyCapture = (e: Event) => {
      const ke = e as KeyboardEvent;
      if (!PRESENTATION_NAV_KEYS.has(ke.key)) return;
      const active = doc.activeElement as HTMLElement | null;
      if (!active?.isContentEditable || !getEditableIsland(active)) return;
      ke.stopPropagation();
      ke.stopImmediatePropagation();
    };

    doc.addEventListener("focusin", onFocusIn);
    doc.addEventListener("focusout", onFocusOut);
    doc.addEventListener("input", onInput);
    doc.addEventListener("keydown", onKeyDown);
    doc.addEventListener("paste", onPaste);
    navKeyTarget.addEventListener("keydown", onNavKeyCapture, true);
    navKeyTarget.addEventListener("keyup", onNavKeyCapture, true);

    const onAssetError = (e: Event) => {
      const target = e.target;
      if (!target || target === doc.defaultView) return;
      const el = target as HTMLElement & { src?: string; href?: string };
      const url = el.src || el.href;
      if (!url || typeof url !== "string") return;
      handleAssetErrorRef.current(url);
    };
    doc.addEventListener("error", onAssetError, true);

    const cleanupSelection = attachIframeTextSelectionListeners({
      iframeEl,
      enabled: textSelectionEnabled,
      onSelectionChange: handleIframeSelectionChange,
    });

    iframeCleanupRef.current = () => {
      try {
        doc.removeEventListener("focusin", onFocusIn);
        doc.removeEventListener("focusout", onFocusOut);
        doc.removeEventListener("input", onInput);
        doc.removeEventListener("keydown", onKeyDown);
        doc.removeEventListener("paste", onPaste);
        navKeyTarget.removeEventListener("keydown", onNavKeyCapture, true);
        navKeyTarget.removeEventListener("keyup", onNavKeyCapture, true);
        doc.removeEventListener("error", onAssetError, true);
      } catch {}
      cleanupSelection();
    };
  }, [getEditableIsland, textSelectionEnabled, handleIframeSelectionChange]);

  const ensureIframeInteractions = useCallback(() => {
    setTimeout(() => {
      setupIframeInteractions();
    }, 0);
  }, [setupIframeInteractions]);

  const ensureLayoutReflow = useCallback(() => {
    setTimeout(() => {
      iframeRef.current?.contentDocument?.documentElement.classList.add(
        "__cohesive_kick",
      );
      iframeRef.current?.contentDocument?.documentElement.classList.remove(
        "__cohesive_kick",
      );
    }, 0);
  }, []);

  const handleIframeLoad = useCallback(() => {
    if (!hasEverShownContentRef.current) {
      hasEverShownContentRef.current = true;
      setIframeLoaded(true);
      setIsFirstLoading(false);
    }

    setActiveIslandId(null);
    autosaveHandlersRef.current.resetAutosave();

    ensureIframeInteractions();
  }, [ensureIframeInteractions]);

  useEffect(() => {
    if (!srcDoc || !iframeLoaded) return;
    ensureIframeInteractions();
  }, [srcDoc, iframeLoaded, ensureIframeInteractions]);

  useEffect(() => {
    return () => {
      try {
        iframeCleanupRef.current?.();
      } catch {}
      iframeCleanupRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (isPreviewing) {
      setHtmlSource(null);
      return () => setHtmlSource(null);
    }
    if (!record.url || bodyFetchFailed || !htmlMeta) {
      setHtmlSource(null);
      return;
    }
    setHtmlSource(htmlMeta.body);
    return () => {
      setHtmlSource(null);
    };
  }, [isPreviewing, record.url, htmlMeta, bodyFetchFailed, setHtmlSource]);

  useEffect(() => {
    if (isPreviewing) return;
    if (bodyFetchFailed) {
      setFetchError("Failed to load HTML");
      setIsFirstLoading(false);
      return;
    }

    if (!htmlMeta) {
      if (!hasEverShownContentRef.current) {
        setIsFirstLoading(!!record.url);
      }
      return;
    }

    if (
      hasEverShownContentRef.current &&
      Date.now() - justSavedTimestampRef.current < IGNORE_SSE_AFTER_SAVE_MS
    ) {
      return;
    }

    if (!hasEverShownContentRef.current) {
      setIsFirstLoading(true);
    }

    let cancelled = false;

    void (async () => {
      try {
        const { html: rewritten, presignedUrlToId } =
          await rewritePublicFileUrls(
            htmlMeta.body,
            htmlMeta.referencedIds,
            record.id,
            knownMissingPresignIdsRef.current,
          );
        if (cancelled) return;

        presignedUrlToIdRef.current = presignedUrlToId;
        setFetchError(null);

        const fingerprint = computeHtmlSrcDocFingerprint(
          htmlMeta.body,
          record.content_hash,
          knownMissingPresignIdsRef.current,
        );
        if (fingerprint === srcDocFingerprintRef.current) return;

        // Viewers (no edit permission) get a non-editable iframe; the region
        // patch endpoint also enforces this server-side. Default unknown
        // capability to editable (fail-open) — list/mutation cache writes drop
        // can_edit on this record's per-id entry, and failing closed would make
        // the editor intermittently go read-only. Server stays authoritative.
        const prepared = prepareSrcDocHtml(rewritten, {
          editable: record.can_edit ?? true,
        });
        srcDocFingerprintRef.current = fingerprint;

        const doc = getIframeDoc();
        if (hasEverShownContentRef.current && doc?.documentElement) {
          try {
            morphPreparedHtmlIntoDocument(doc, prepared, {
              protectedEditIds: autosaveHandlersRef.current.getDirtyEditIds(),
              userIsEditing: activeIslandIdRef.current !== null,
            });
          } catch (err) {
            Sentry.captureException(err, {
              tags: { feature: "html-iframe-morph" },
              extra: { fileRecordId: record.id },
            });
            setSrcDoc(prepared);
          }
        } else {
          setSrcDoc(prepared);
        }

        ensureIframeInteractions();
        ensureLayoutReflow();
      } catch (err) {
        if (cancelled) return;
        setFetchError(err instanceof Error ? err.message : "Failed to load");
        setIsFirstLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    htmlMeta,
    bodyFetchFailed,
    record.url,
    record.id,
    record.content_hash,
    isPreviewing,
    presignRetryNonce,
    getIframeDoc,
    ensureIframeInteractions,
  ]);

  if (isPreviewing) {
    if (!previewMediaUrl) {
      return (
        <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
          Version preview URL not available for this file.
        </div>
      );
    }
    return (
      <div className="relative h-full w-full">
        <iframe
          key={previewMediaUrl}
          src={previewMediaUrl}
          title={getFileRecordName(record)}
          className={cn(
            "h-full w-full border-0 transition-opacity duration-300",
            previewLoaded ? "opacity-100" : "opacity-0",
          )}
          sandbox="allow-same-origin allow-scripts allow-popups"
          onLoad={() => setTimeout(() => setPreviewLoaded(true), 300)}
        />
        {!previewLoaded && <LoadingOverlay />}
      </div>
    );
  }

  if (!record.url && !isFirstLoading) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        HTML not available.
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-destructive">
        Failed to load HTML: {fetchError}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full"
      style={{ colorScheme: "light" }}
    >
      {srcDoc && (
        <iframe
          ref={iframeRef}
          srcDoc={srcDoc}
          title={getFileRecordName(record)}
          className={cn(
            "w-full h-full border-0 transition-opacity duration-300 bg-paper",
            iframeLoaded ? "opacity-100" : "opacity-0",
          )}
          style={{ colorScheme: "light" }}
          sandbox="allow-same-origin allow-scripts allow-popups"
          onLoad={handleIframeLoad}
        />
      )}

      {isFirstLoading && <LoadingOverlay />}

      {textSelectionEnabled && (
        <FileRecordSelectionActionOverlay
          selectedText={iframeSelectionText}
          anchorRect={iframeSelectionAnchor}
          onActivate={pushSelection}
          onClear={clearIframeSelection}
        />
      )}
    </div>
  );
}
